// Best-effort rewrite of cross-service URL references when a service is
// renamed. The dev runner injects sibling URLs as `<UPPER_NAME>_URL` env
// vars (decision 015); user code typically references them as
// `process.env.WEB_URL` (or `os.environ['WEB_URL']` in python). When a
// rename happens, those references must move to the new name or the user
// gets silent runtime breakage on next `grid dev`.
//
// Scope: walk `services/*/src/**` and replace whole-token occurrences of
// `<UPPER_OLD>_URL` with `<UPPER_NEW>_URL`. Hits in `services/<oldname>/`
// (about-to-be-renamed) are not skipped — `git mv` happens after this
// rewrite so its files still live at the old path during scan.
//
// Not: a full grep across the entire repo. The user can always run their
// own search if they reference the var elsewhere.

import { readFileSync, readdirSync, statSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import { upperSnake } from '../../utils.js';

export interface RewriteSummary {
  /** Files that were touched, with the count of replacements per file. */
  edits: Array<{ path: string; replacements: number }>;
  /** Files scanned but unchanged. */
  scanned: number;
}

const TEXT_EXTS = new Set([
  '.js',
  '.mjs',
  '.cjs',
  '.ts',
  '.tsx',
  '.jsx',
  '.py',
  '.json',
  '.yaml',
  '.yml',
  '.md',
  '.html',
  '.css',
  '.env',
]);

function shouldDescend(name: string): boolean {
  if (name.startsWith('.')) return false;
  if (name === 'node_modules') return false;
  if (name === '__pycache__') return false;
  if (name === 'dist') return false;
  if (name === 'build') return false;
  return true;
}

function collectFiles(root: string): string[] {
  const result: string[] = [];
  const walk = (dir: string): void => {
    let entries: string[];
    try { entries = readdirSync(dir); } catch { return; }
    for (const entry of entries) {
      const full = join(dir, entry);
      let stat;
      try { stat = statSync(full); } catch { continue; }
      if (stat.isDirectory()) {
        if (shouldDescend(entry)) walk(full);
        continue;
      }
      const dotIdx = entry.lastIndexOf('.');
      if (dotIdx === -1) continue;
      const ext = entry.slice(dotIdx).toLowerCase();
      if (TEXT_EXTS.has(ext)) result.push(full);
    }
  };
  walk(root);
  return result;
}

/**
 * Rewrite every occurrence of `<UPPER_OLD>_URL` to `<UPPER_NEW>_URL` under
 * `<cwd>/services/`. Returns a summary the caller renders to the user.
 *
 * Token boundaries: the regex requires non-word characters (or string
 * boundaries) on each side so e.g. `WEBHOOK_URL` is not mistaken for a hit
 * on `WEB_URL`.
 */
export function rewriteSiblingUrlRefs(
  cwd: string,
  oldName: string,
  newName: string,
): RewriteSummary {
  const oldVar = `${upperSnake(oldName)}_URL`;
  const newVar = `${upperSnake(newName)}_URL`;
  const re = new RegExp(`(^|\\W)${oldVar}(\\W|$)`, 'g');

  const summary: RewriteSummary = { edits: [], scanned: 0 };
  const root = join(cwd, 'services');
  const files = collectFiles(root);
  for (const file of files) {
    summary.scanned++;
    let content: string;
    try { content = readFileSync(file, 'utf-8'); } catch { continue; }
    let count = 0;
    const updated = content.replace(re, (_match, lead: string, trail: string) => {
      count++;
      return `${lead}${newVar}${trail}`;
    });
    if (count > 0 && updated !== content) {
      writeFileSync(file, updated);
      summary.edits.push({ path: file, replacements: count });
    }
  }
  return summary;
}
